import { z } from "zod";
import { COUPON_DISCOUNT_TYPES, COUPON_STATUSES, INVOICE_STATUSES } from "../constants/domain";
import { VALIDATION_MESSAGES } from "../content/validation-messages";
import { paginationQuerySchema } from "./pagination";

export const couponInputSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(3)
      .max(64)
      .regex(/^[A-Za-z0-9_-]+$/, VALIDATION_MESSAGES.couponCodeFormat)
      .transform((code) => code.toUpperCase()),
    status: z.enum(COUPON_STATUSES),
    discountType: z.enum(COUPON_DISCOUNT_TYPES),
    discountValue: z.number().int().min(1).max(1_000_000_000),
    remark: z.string().trim().max(500).nullable().optional(),
    maxUses: z.number().int().min(1).nullable().optional(),
    startsAt: z.iso.datetime().nullable().optional(),
    endsAt: z.iso.datetime().nullable().optional(),
  })
  .refine((input) => input.discountType !== "percentage" || input.discountValue <= 100, {
    message: VALIDATION_MESSAGES.percentageMax,
    path: ["discountValue"],
  })
  .refine((input) => !input.startsAt || !input.endsAt || input.startsAt < input.endsAt, {
    message: VALIDATION_MESSAGES.endAfterStart,
    path: ["endsAt"],
  });
export type CouponInput = z.infer<typeof couponInputSchema>;

export const adminInvoiceListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(INVOICE_STATUSES).optional(),
  /** Invoice number or buyer email. */
  search: z.string().trim().max(120).optional(),
});
export type AdminInvoiceListQuery = z.infer<typeof adminInvoiceListQuerySchema>;

export const markPaidSchema = z.object({
  reason: z.string().trim().min(5, VALIDATION_MESSAGES.reasonRequired).max(500),
});

export const adminUserListQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuerySchema>;

export const adminUserUpdateSchema = z.object({
  email: z.email().max(320).transform((email) => email.toLowerCase()).optional(),
  fullName: z.string().trim().min(1).max(160).nullable().optional(),
  phone: z.string().trim().max(32).nullable().optional(),
});
export type AdminUserUpdate = z.infer<typeof adminUserUpdateSchema>;

export const grantProductSchema = z.object({ productId: z.uuid() });

export const UPLOAD_PURPOSES = ["image", "content"] as const;
export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number];

/** Images go to the public bucket served by img.jipraks.com; content files stay private. */
export const UPLOAD_LIMITS: Record<UploadPurpose, { maxBytes: number; contentTypes: readonly string[] }> = {
  image: { maxBytes: 5 * 1024 * 1024, contentTypes: ["image/png", "image/jpeg", "image/webp", "image/gif"] },
  content: {
    maxBytes: 5 * 1024 * 1024 * 1024,
    contentTypes: ["video/mp4", "video/webm", "application/pdf", "application/json", "text/plain", "application/zip", "application/x-zip-compressed"],
  },
};

export const uploadRequestSchema = z
  .object({
    purpose: z.enum(UPLOAD_PURPOSES),
    fileName: z.string().trim().min(1).max(200),
    contentType: z.string().min(3).max(120),
    size: z.number().int().min(1),
  })
  .superRefine((input, context) => {
    const limits = UPLOAD_LIMITS[input.purpose];
    if (!limits.contentTypes.includes(input.contentType)) {
      context.addIssue({ code: "custom", path: ["contentType"], message: VALIDATION_MESSAGES.unsupportedFileType });
    }
    if (input.size > limits.maxBytes) {
      context.addIssue({ code: "custom", path: ["size"], message: VALIDATION_MESSAGES.fileTooLarge });
    }
  });
export type UploadRequest = z.infer<typeof uploadRequestSchema>;
